/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { StepperComponent } from '../../../shared/components/stepper/stepper.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CodCreateFormGroup } from '../../models/cod-create.model';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { StepConfig } from '../../../shared/types/step-config.type';
import { select, Store } from '@ngxs/store';
import { CodState } from '../../state/cod.state';
import { FormType } from '@cod-monorepo/interfaces';
import { CopyCod, EvaluateSteps, RemoveTopErrorMessage, ResetForm } from '../../state/cod.action';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    filter,
    map,
    Observable,
    ReplaySubject,
    startWith,
    takeUntil,
    tap
} from 'rxjs';
import { CodSummaryStepComponent } from '../steps/summary/summary.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { WorkflowError } from '../../../shared/enums/workflow-error';
import { ViewMode } from '../../../shared/enums/view-mode';
import { CodSubmittingStepComponent } from '../steps/submitting/submitting.component';
import { CancelCodCreationDialogComponent } from '../dialogs/cancel-application/cancel-application.component';
import { ButtonModule, DialogModule, DialogService } from '@sag-ui';
import { CodCreateFormComponent } from '../cod-create-form/cod-create-form.component';
import { CodCreateOperations } from '../../operations/cod-create.operations';
import { GeneralCod } from '../../models/cod.model';
import { CodDetailCardComponent } from '../cod-detail-card/cod-detail-card.component';

@Component({
    selector: 'app-cod-create',
    templateUrl: './cod-create.component.html',
    imports: [
        CommonModule,
        StepperComponent,
        FormsModule,
        ReactiveFormsModule,
        NgxsFormDirective,
        ButtonModule,
        RouterModule,
        CodSummaryStepComponent,
        CodSubmittingStepComponent,
        DialogModule,
        CodCreateFormComponent,
        CodDetailCardComponent
    ],
    standalone: true
})
export class CodCreateComponent implements OnInit, OnDestroy {
    @ViewChild('stepsContainer') stepsContainer!: ElementRef;

    public codLines: Signal<GeneralCod[]> = select(CodState.mappedCodLines);
    public disableValidation: Signal<boolean> = select(CodState.disableValidation);
    public steps: Signal<StepConfig[]> = select(CodState.steps);
    public steps$: Observable<StepConfig[]> = inject(Store).select(CodState.steps);
    public errorMessages: Signal<WorkflowError[]> = select(CodState.errorMessages);
    public viewMode: Signal<ViewMode> = select(CodState.viewMode);
    public formGroup: FormGroup<CodCreateFormGroup> | null = null;
    private destroy$ = new ReplaySubject<void>(1);
    public formType = FormType;
    public viewModes = ViewMode;
    public copyId: string;

    constructor(
        private store: Store,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private dialogService: DialogService
    ) {}

    ngOnInit(): void {
        this.initializeForm();
        this.initStepsChangeListener();
        this.initCopyChangeListener();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected initializeForm(): void {
        this.store.dispatch(new ResetForm());
        this.formGroup = CodCreateOperations.initializeForm();
    }

    protected confirmStep(): void {
        this.store.dispatch(new EvaluateSteps());
    }

    protected popErrorMessage(): void {
        this.store.dispatch(new RemoveTopErrorMessage());
    }

    protected cancelCreation(): void {
        const dialogRef = this.dialogService.open(CancelCodCreationDialogComponent, {});

        dialogRef.afterClosed.subscribe(result => {
            if (result) {
                this.store.dispatch(new ResetForm());
                this.router.navigate(['/']);
            }
        });
    }

    private initStepsChangeListener(): void {
        this.steps$
            .pipe(
                startWith([]),
                distinctUntilChanged(),
                filter(() => this.stepsContainer && this.stepsContainer.nativeElement),
                debounceTime(200),
                tap(() =>
                    this.stepsContainer.nativeElement.scrollTo({
                        top: this.stepsContainer.nativeElement.scrollHeight,
                        behavior: 'smooth'
                    })
                )
            )
            .subscribe();
    }

    private initCopyChangeListener(): void {
        this.activatedRoute.queryParams
            .pipe(
                takeUntil(this.destroy$),
                filter(params => !!params['copy']),
                map(params => params['copy'] as string),
                tap((id: string) => {
                    this.copyId = id;
                    this.store.dispatch(new CopyCod(id));
                }),
                catchError(error => {
                    console.error(error);
                    return EMPTY;
                })
            )
            .subscribe();
    }
}
